////////////////////////////////////////////
// ROUTES — URL CONTRACT (POST-CUTOVER)
////////////////////////////////////////////
//
// LOAD-BEARING URL RULES (DO NOT BREAK — LINKS ARE IN THE WILD):
//   /?launch=<key>   -> LAUNCH DETAIL (RACE PAGE); NO ?launch -> HOME
//   /launch/<addr>   -> SAME RACE PAGE, INDEXABLE PATH FORM (Solana only —
//                       base58 addr, no ":"). SERVER-RENDERS REAL meta/OG/
//                       canonical + a crawlable summary before the SPA mounts
//                       (web/server/handlers.mjs handleLaunchPage) — THE
//                       CANONICAL URL FOR SHARING/SEO; ?launch= KEEPS WORKING
//                       (OLD LINKS) BUT SELF-CANONICALIZES TO THIS FORM.
//   ?net=            -> mainnet (OMITTED) | devnet | rh | rh-testnet
//   ?chain=          -> LEGACY solana | rh — READ FOREVER, NEVER EMITTED
//   <key> SHAPE      -> base58 = SOLANA, 0xfactory:id = ROBINHOOD CHAIN
//   /l/<prefix>, /og/<launch>.png, /m/<id>.json -> SERVER-SIDE, NEVER OURS
//   /sample          -> RETIRED DEMO PAGE; REDIRECTS TO /discover
//
// EVERY PAGE IS CODE-SPLIT (React.lazy) SO THE INITIAL BUNDLE STAYS SMALL —
// WALLET-HEAVY PAGES ONLY LOAD THEIR CHUNKS WHEN VISITED. UNKNOWN PATHS
// REDIRECT HOME WITH THE QUERY STRING PRESERVED.
import { createBrowserRouter, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import { PageShell } from "@/app/layout/PageShell";

// LAZY PAGES — NAMED EXPORTS WRAPPED FOR React.lazy
const HomePage = lazy(() => import("@/features/home/HomePage").then((m) => ({ default: m.HomePage })));
const LaunchPage = lazy(() => import("@/features/launch/LaunchPage").then((m) => ({ default: m.LaunchPage })));
const DiscoverPage = lazy(() => import("@/features/discover/DiscoverPage").then((m) => ({ default: m.DiscoverPage })));
const CreatePage = lazy(() => import("@/features/create/CreatePage").then((m) => ({ default: m.CreatePage })));
const CreateInstantPage = lazy(() => import("@/features/create/CreateInstantPage").then((m) => ({ default: m.CreateInstantPage })));
const HowItWorksPage = lazy(() => import("@/features/howitworks/HowItWorksPage").then((m) => ({ default: m.HowItWorksPage })));
const LegalPage = lazy(() => import("@/features/legal/LegalPage").then((m) => ({ default: m.LegalPage })));
const LoginPage = lazy(() => import("@/features/login/LoginPage").then((m) => ({ default: m.LoginPage })));
const AdminLayout = lazy(() => import("@/features/admin/shell/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const AdminOverviewPage = lazy(() => import("@/features/admin/overview/AdminOverviewPage").then((m) => ({ default: m.AdminOverviewPage })));
const AdminLaunchesPage = lazy(() => import("@/features/admin/launches/AdminLaunchesPage").then((m) => ({ default: m.AdminLaunchesPage })));
const AdminFinancialsPage = lazy(() => import("@/features/admin/financials/AdminFinancialsPage").then((m) => ({ default: m.AdminFinancialsPage })));
const AdminSessionsPage = lazy(() => import("@/features/admin/sessions/AdminSessionsPage").then((m) => ({ default: m.AdminSessionsPage })));
const AdminLogsPage = lazy(() => import("@/features/admin/logs/AdminLogsPage").then((m) => ({ default: m.AdminLogsPage })));
const AdminLogSessionPage = lazy(() => import("@/features/admin/logs/SessionPage").then((m) => ({ default: m.SessionPage })));
const AdminUsersPage = lazy(() => import("@/features/admin/users/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })));
const AdminConfigPage = lazy(() => import("@/features/admin/config/AdminConfigPage").then((m) => ({ default: m.AdminConfigPage })));

// ROOT SWITCH — HOME AND THE RACE PAGE SHARE "/", DISCRIMINATED BY ?launch
function HomeOrLaunch() {

  const [params] = useSearchParams();
  return params.get("launch") ? <LaunchPage /> : <HomePage />;
}

// UNKNOWN PATH — HOME, QUERY PRESERVED
function ToHome() {

  const { search } = useLocation();
  return <Navigate to={"/" + search} replace />;
}

// SUSPENSE WRAPPER — MINIMAL FALLBACK; PAGES RENDER THEIR OWN SKELETONS
function page(el: React.ReactNode) {

  return <Suspense fallback={<div className="wrap" />}>{el}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: <PageShell />,
    children: [
      { path: "/", element: page(<HomeOrLaunch />) },
      { path: "/launch/:addr", element: page(<LaunchPage />) },
      { path: "/discover", element: page(<DiscoverPage />) },
      { path: "/create", element: page(<CreatePage />) },
      { path: "/create-instant", element: page(<CreateInstantPage />) },
      { path: "/howitworks", element: page(<HowItWorksPage />) },
      { path: "/legal", element: page(<LegalPage />) },
      { path: "/login", element: page(<LoginPage />) },
      // RETIRED SAMPLE PAGE — OLD LINKS LAND ON THE LIVE LAUNCHPAD
      { path: "/sample", element: <Navigate to="/discover" replace /> },

      // ADMIN PORTAL — SHELL + SEVEN PAGES
      {
        path: "/admin",
        element: page(<AdminLayout />),
        children: [
          { index: true, element: page(<AdminOverviewPage />) },
          { path: "launches", element: page(<AdminLaunchesPage />) },
          { path: "financials", element: page(<AdminFinancialsPage />) },
          { path: "sessions", element: page(<AdminSessionsPage />) },
          { path: "logs", element: page(<AdminLogsPage />) },
          { path: "logs/session/:sid", element: page(<AdminLogSessionPage />) },
          { path: "users", element: page(<AdminUsersPage />) },
          { path: "config", element: page(<AdminConfigPage />) },
        ],
      },

      { path: "*", element: <ToHome /> },
    ],
  },
]);
